import { gql } from "@apollo/client";
import type {
  EpocusBillingDocumentationStatus,
  EpocusBillingMode,
  EpocusBillingSyncPushDocument,
} from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { EpocusBilling } from "../collections/epocus-billing";
import type { RxDBCollections } from "../provider";
import {
  SyncEpocusBillingPushDocument,
  type WholeSyncableEpocusBillingFragment,
} from "./__generated__/epocus-billing.gql";
import { useGqlReplication } from "./use-gql-replication";

export const useReplicateEpocusBilling = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<EpocusBilling>;
}) => {
  const push = useMemo(
    () => ({
      mutation: SyncEpocusBillingPushDocument,
      mapRxToGql: mapRxEpocusBillingToGqlInput,
    }),
    [],
  );

  return useGqlReplication<
    EpocusBilling,
    WholeSyncableEpocusBillingFragment,
    EpocusBillingSyncPushDocument
  >({
    db,
    collection,
    push,
    mapGqlToRx: mapGqlEpocusBillingToRx,
    batchSize: 100,
    expireData: false,
    metadataLocalStorageKeySuffix: "epocus-billing",
  });
};

export const mapGqlEpocusBillingToRx = (
  doc: WholeSyncableEpocusBillingFragment,
): WithDeleted<EpocusBilling> => ({
  id: doc.id,
  examId: doc.examId ?? null,
  encounterId: doc.encounterId,
  dateOfService: doc.dateOfService ?? null,
  patientMrn: doc.patientMrn ?? null,
  patientDob: doc.patientDob ?? null,
  payerName: doc.payerName ?? null,
  policyNumber: doc.policyNumber ?? null,
  performingPhysicianName: doc.performingPhysicianName ?? null,
  performingPhysicianNpi: doc.performingPhysicianNpi ?? null,
  supervisingPhysicianNpi: doc.supervisingPhysicianNpi ?? null,
  facilityNpi: doc.facilityNpi ?? null,
  clinicalSetting: (doc.clinicalSetting ??
    null) as EpocusBilling["clinicalSetting"],
  billingMode: (doc.billingMode ?? null) as EpocusBilling["billingMode"],
  procedureCodes: doc.procedureCodes as EpocusBilling["procedureCodes"],
  diagnosisCodes: doc.diagnosisCodes as EpocusBilling["diagnosisCodes"],
  documentationStatus: (doc.documentationStatus ??
    null) as EpocusBilling["documentationStatus"],
  imagesStored: doc.imagesStored,
  exportedAt: doc.exportedAt ?? null,
  locked: doc.locked,
  meta: {
    createdAt: doc.meta.createdAt,
    updatedAt: doc.meta.updatedAt,
    createdByUserId: doc.meta.createdByUserId,
    updatedByUserId: doc.meta.updatedByUserId,
  },
  _deleted: doc.meta.deleted,
});

export const mapRxEpocusBillingToGqlInput = (
  doc: WithDeleted<EpocusBilling>,
): EpocusBillingSyncPushDocument => ({
  id: doc.id,
  examId: doc.examId,
  encounterId: doc.encounterId,
  dateOfService: doc.dateOfService,
  patientMrn: doc.patientMrn,
  patientDob: doc.patientDob,
  payerName: doc.payerName,
  policyNumber: doc.policyNumber,
  performingPhysicianName: doc.performingPhysicianName,
  performingPhysicianNpi: doc.performingPhysicianNpi,
  supervisingPhysicianNpi: doc.supervisingPhysicianNpi,
  facilityNpi: doc.facilityNpi,
  clinicalSetting: doc.clinicalSetting,
  billingMode: doc.billingMode as EpocusBillingMode | null,
  procedureCodes: doc.procedureCodes,
  diagnosisCodes: doc.diagnosisCodes,
  documentationStatus:
    doc.documentationStatus as EpocusBillingDocumentationStatus | null,
  imagesStored: doc.imagesStored,
  exportedAt: doc.exportedAt,
  locked: doc.locked,
  meta: {
    createdAt: doc.meta.createdAt,
    updatedAt: doc.meta.updatedAt,
    createdByUserId: doc.meta.createdByUserId,
    updatedByUserId: doc.meta.updatedByUserId,
    deleted: doc._deleted,
  },
});

gql`
  fragment WholeSyncableEpocusBilling on SyncableEpocusBilling {
    id
    examId
    encounterId
    dateOfService
    patientMrn
    patientDob
    payerName
    policyNumber
    performingPhysicianName
    performingPhysicianNpi
    supervisingPhysicianNpi
    facilityNpi
    clinicalSetting
    billingMode
    procedureCodes
    diagnosisCodes
    documentationStatus
    imagesStored
    exportedAt
    locked
    meta {
      createdAt
      updatedAt
      createdByUserId
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncEpocusBillingPull($input: PullEpocusBillingInput!) {
    pullEpocusBilling(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableEpocusBilling
      }
    }
  }
`;

gql`
  mutation SyncEpocusBillingPush($input: PushEpocusBillingInput!) {
    pushEpocusBilling(input: $input) {
      rows {
        ...WholeSyncableEpocusBilling
      }
    }
  }
`;
